import { computed, ref } from "vue";
import Sortable from "sortablejs";

const UNASSIGNED_BOARD_ID = "_unassigned";
let newRoutePlaceholderCounter = 1;
let stopCardCounter = 1;

function createBoardUiDefaults(progress = 0) {
  return {
    stops_counter: 0,
    progress,
  };
}

function createUnassignedBoard() {
  return {
    id: UNASSIGNED_BOARD_ID,
    type: "unassigned",
    isPlaceholder: false,
    apiRoute: null,
    items: [],
    ui: createBoardUiDefaults(0),
  };
}

function createRoutePlaceholderBoard() {
  const placeholderId = `_new_route_${Date.now()}_${newRoutePlaceholderCounter++}`;

  return {
    id: placeholderId,
    type: "placeholder",
    isPlaceholder: true,
    apiRoute: null,
    items: [],
    ui: createBoardUiDefaults(0),
  };
}

const vehicleOptions = ref([]);
const routeBoards = ref([createUnassignedBoard(), createRoutePlaceholderBoard()]);
const dragSnapshot = ref(null);

function toSafeString(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function toSafeArray(value) {
  return Array.isArray(value) ? value : [];
}

function isPendingStatus(status) {
  return toSafeString(status).toUpperCase() === "PENDIENTE";
}

function calculateRouteProgress(stopCards) {
  if (!Array.isArray(stopCards) || stopCards.length === 0) return 0;

  const completedStops = stopCards.reduce((count, stopCard) => {
    return isPendingStatus(stopCard?.apiStop?.status) ? count : count + 1;
  }, 0);

  return Number(((completedStops / stopCards.length) * 100).toFixed(2));
}

function toOrderNumberFromIndex(index) {
  const parsed = Number.parseInt(index, 10);
  if (!Number.isInteger(parsed) || parsed < 0) return 1;
  return parsed + 1;
}

function parseIndex(value) {
  if (Number.isInteger(value)) return value;
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) ? parsed : -1;
}

function cloneBoards(value) {
  return toSafeArray(value).map((board) => ({
    ...board,
    ui: {
      ...(board?.ui || createBoardUiDefaults(0)),
    },
    items: toSafeArray(board?.items).slice(),
  }));
}

function isUnassignedBoard(board) {
  return board?.id === UNASSIGNED_BOARD_ID;
}

function resolveRouteId(routeData) {
  return routeData?._id || "";
}

function resolveVehicleId(routeData) {
  return routeData?.vehicle_id || "";
}

function resolveStopId(stopData) {
  return stopData?._id || "";
}

function resolveRouteStopId(stopData) {
  return stopData?._id || "";
}

function buildStopCardId(stopData, boardId, index) {
  const directId = stopData?._id || stopData?.stop_id || stopData?.id || "";
  if (directId) return `${boardId}::${directId}`;

  const fallback = `${boardId}::idx_${index}_${stopCardCounter++}`;
  return fallback;
}

function createStopCard(stopData, boardId, index) {
  return {
    id: buildStopCardId(stopData, boardId, index),
    apiStop: stopData,
  };
}

function createRouteBoard(routeData, index) {
  const routeId = resolveRouteId(routeData) || `route_${index + 1}`;
  const routeStops = toSafeArray(routeData?.routes);

  return {
    id: routeId,
    type: "route",
    isPlaceholder: false,
    apiRoute: routeData,
    items: routeStops.map((stopData, stopIndex) => createStopCard(stopData, routeId, stopIndex)),
    ui: createBoardUiDefaults(0),
  };
}

const unassignedStopsBoard = computed(() => routeBoards.value.find((board) => isUnassignedBoard(board)) || null);
const assignedRouteBoards = computed(() => routeBoards.value.filter((board) => !isUnassignedBoard(board)));

function getRouteDragGroup(routeBoard) {
  if (routeBoard?.isPlaceholder) {
    return {
      name: "tasks",
      pull: false,
      put: false,
    };
  }

  return {
    name: "tasks",
    pull: true,
    put: true,
  };
}

function getRouteAccentColor(board) {
  const routeData = board?.apiRoute || {};
  const routeColor = routeData?.route_color_hex || "";
  if (routeColor) return routeColor;

  const vehicleId = resolveVehicleId(routeData);
  const selectedVehicle = vehicleOptions.value.find((x) => x.value === vehicleId);
  if (selectedVehicle?.color) return selectedVehicle.color;

  return "#5baee9";
}

function syncRouteBoardStats() {
  routeBoards.value.forEach((board) => {
    const boardItems = toSafeArray(board?.items);
    if (!board.ui || typeof board.ui !== "object") {
      board.ui = createBoardUiDefaults(0);
    }

    board.ui.stops_counter = boardItems.length;

    if (isUnassignedBoard(board) || board?.isPlaceholder) {
      board.ui.progress = 0;
      return;
    }

    board.ui.progress = calculateRouteProgress(boardItems);
  });
}

function getRouteVehicleContext(board) {
  const routeData = board?.apiRoute || {};
  const routeId = resolveRouteId(routeData) || board?.id || "";

  return {
    id: routeId,
    _id: routeId,
    name: routeData?.name,
    vehicle_id: routeData?.vehicle_id,
    progress: board?.ui?.progress ?? 0,
    stops_counter: board?.ui?.stops_counter ?? toSafeArray(board?.items).length,
    km: routeData?.km ?? routeData?.distance,
    time_window: routeData?.time_window,
    total_time: routeData?.total_time,
  };
}

function ensureUnassignedStopsBoard() {
  const board = routeBoards.value.find((item) => isUnassignedBoard(item));
  if (board) return board;

  const created = createUnassignedBoard();
  routeBoards.value.unshift(created);
  return created;
}

function ensureRoutePlaceholderBoard() {
  const board = routeBoards.value.find((item) => item?.isPlaceholder);
  if (board) return board;

  const created = createRoutePlaceholderBoard();
  routeBoards.value.push(created);
  return created;
}

function getRouteBoardById(boardId, sourceBoards = routeBoards.value) {
  return toSafeArray(sourceBoards).find((board) => board.id === boardId) || null;
}

function findStopInRouteBoard(board, taskId, index = -1) {
  if (!board || !Array.isArray(board.items)) return null;

  if (taskId) {
    const byId = board.items.find((stopCard) => {
      if (toSafeString(stopCard?.id) === taskId) return true;

      const stopData = stopCard?.apiStop || {};
      const ids = [stopData?._id, stopData?.stop_id, stopData?.id];
      return ids.some((candidate) => toSafeString(candidate) === taskId);
    });
    if (byId) return byId;
  }

  if (Number.isInteger(index) && index >= 0 && index < board.items.length) {
    return board.items[index];
  }

  return null;
}

function extractRoutesFromList(list) {
  if (!Array.isArray(list)) return null;
  const planWithRoutes = list.find((item) => Array.isArray(item?.routes));
  if (Array.isArray(planWithRoutes?.routes)) return planWithRoutes.routes;
  return null;
}

function extractRoutesFromResponse(response) {
  const source = response?.data || response || {};

  if (Array.isArray(source?.routes)) return source.routes;
  if (Array.isArray(source?.plan?.routes)) return source.plan.routes;
  if (Array.isArray(source?.data?.routes)) return source.data.routes;

  const fromSourceArray = extractRoutesFromList(source);
  if (Array.isArray(fromSourceArray)) return fromSourceArray;

  const fromItems = extractRoutesFromList(source?.items);
  if (Array.isArray(fromItems)) return fromItems;

  const fromDocs = extractRoutesFromList(source?.docs);
  if (Array.isArray(fromDocs)) return fromDocs;

  const fromDataItems = extractRoutesFromList(source?.data?.items);
  if (Array.isArray(fromDataItems)) return fromDataItems;

  const fromDataDocs = extractRoutesFromList(source?.data?.docs);
  if (Array.isArray(fromDataDocs)) return fromDataDocs;

  return null;
}

function hydrateRouteBoardsFromResponse(response) {
  const routes = extractRoutesFromResponse(response);
  if (!Array.isArray(routes)) {
    throw new Error("La respuesta no incluye rutas actualizadas del kanban.");
  }

  const previousBoards = cloneBoards(routeBoards.value);
  const unassigned = getRouteBoardById(UNASSIGNED_BOARD_ID, previousBoards) || ensureUnassignedStopsBoard();
  const placeholders = previousBoards.filter((routeBoard) => routeBoard?.isPlaceholder);

  const apiAssigned = routes.map((routeData, index) => createRouteBoard(routeData, index));

  routeBoards.value = [unassigned, ...apiAssigned, ...placeholders];
  ensureRoutePlaceholderBoard();
  syncRouteBoardStats();
  clearAllSortableSelections();
}

function setVehicleOptions(options) {
  vehicleOptions.value = toSafeArray(options)
    .map((item) => ({
      value: item?.value || "",
      label: item?.label || "",
      abbreviation: item?.abbreviation || "",
      color: item?.color || "",
    }))
    .filter((item) => Boolean(item.value));
}

function setUnassignedStops(stops) {
  const board = ensureUnassignedStopsBoard();
  board.items = toSafeArray(stops).map((stopData, index) => createStopCard(stopData, UNASSIGNED_BOARD_ID, index));
  syncRouteBoardStats();
}

function setPlanContext(planInfo) {
  if (!planInfo) {
    clearPlanContext();
    return;
  }

  try {
    hydrateRouteBoardsFromResponse(planInfo);
  } catch (error) {
    console.error("No se pudo hidratar el contexto del plan:", error);
    clearPlanContext();
  }
}

function clearPlanContext() {
  routeBoards.value = [createUnassignedBoard(), createRoutePlaceholderBoard()];
  dragSnapshot.value = null;
  syncRouteBoardStats();
  clearAllSortableSelections();
}

function clearAllSortableSelections() {
  if (typeof document === "undefined") return;
  Array.from(document.querySelectorAll(".sortable-selected")).forEach((el) => {
    try { Sortable.utils.deselect(el); } catch (_) { /* ignore */ }
  });
}

function handleDragStart() {
  dragSnapshot.value = cloneBoards(routeBoards.value);
}

function clearDragSnapshot() {
  dragSnapshot.value = null;
}

function restoreDragSnapshot() {
  if (!dragSnapshot.value) return;
  routeBoards.value = cloneBoards(dragSnapshot.value);
  dragSnapshot.value = null;
  syncRouteBoardStats();
}

function resolveDropAction(event) {
  const fromBoardId = toSafeString(event?.from?.dataset?.boardId);
  const toBoardId = toSafeString(event?.to?.dataset?.boardId);
  const movedTaskId = toSafeString(event?.item?.dataset?.taskId);
  const oldIndex = parseIndex(event?.oldIndex);
  const newIndex = parseIndex(event?.newIndex);

  if (!fromBoardId || !toBoardId) {
    clearDragSnapshot();
    return null;
  }

  const fromIsUnassigned = fromBoardId === UNASSIGNED_BOARD_ID;
  const toIsUnassigned = toBoardId === UNASSIGNED_BOARD_ID;
  const sameBoard = fromBoardId === toBoardId;
  const samePosition = oldIndex >= 0 && newIndex >= 0 && oldIndex === newIndex;

  if ((sameBoard && toIsUnassigned) || (sameBoard && samePosition)) {
    clearDragSnapshot();
    return null;
  }

  const sourceRouteBoardSnapshot = getRouteBoardById(fromBoardId, dragSnapshot.value || []);
  const targetRouteBoard = getRouteBoardById(toBoardId);
  const sourceRouteStopSnapshot = findStopInRouteBoard(sourceRouteBoardSnapshot, movedTaskId, oldIndex);
  const destinationRouteStop = findStopInRouteBoard(targetRouteBoard, movedTaskId, newIndex);

  const multiItems = Array.isArray(event?.items) ? event.items.filter(Boolean) : [];
  const isMulti = multiItems.length > 1;

  function collectOrderedCards() {
    const draggedIds = new Set(
      multiItems.map((el) => toSafeString(el?.dataset?.taskId)).filter(Boolean)
    );
    return toSafeArray(sourceRouteBoardSnapshot?.items).filter((card) =>
      draggedIds.has(toSafeString(card.id))
    );
  }

  if (fromIsUnassigned && !toIsUnassigned) {
    if (isMulti) {
      const stopIds = collectOrderedCards()
        .map((card) => resolveStopId(card.apiStop))
        .filter(Boolean);
      if (!stopIds.length) throw new Error("No se encontraron stop_ids para asignar las paradas.");
      return {
        type: "assign-unassigned-stop",
        payload: { routeId: toBoardId, stopIds, newOrder: toOrderNumberFromIndex(newIndex) },
      };
    }
    const stopId = resolveStopId(sourceRouteStopSnapshot?.apiStop);
    if (!stopId) throw new Error("No se encontro stop_id para asignar la tarea.");
    return {
      type: "assign-unassigned-stop",
      payload: { routeId: toBoardId, stopIds: [stopId], newOrder: toOrderNumberFromIndex(newIndex) },
    };
  }

  if (!fromIsUnassigned && toIsUnassigned) {
    if (isMulti) {
      const routeStopIds = collectOrderedCards()
        .map((card) => resolveRouteStopId(card.apiStop))
        .filter(Boolean);
      if (!routeStopIds.length) throw new Error("No se encontraron route_stop_ids para desasignar las paradas.");
      return {
        type: "unassign-route-stop",
        payload: { routeId: fromBoardId, routeStopIds },
      };
    }
    const routeStopId = resolveRouteStopId(sourceRouteStopSnapshot?.apiStop);
    if (!routeStopId) throw new Error("No se encontro _id de route_stop para desasignar la tarea.");
    return {
      type: "unassign-route-stop",
      payload: { routeId: fromBoardId, routeStopIds: [routeStopId] },
    };
  }

  if (!fromIsUnassigned && !toIsUnassigned && !sameBoard) {
    if (isMulti) {
      const routeStopIds = collectOrderedCards()
        .map((card) => resolveRouteStopId(card.apiStop))
        .filter(Boolean);
      if (!routeStopIds.length) throw new Error("No se encontraron route_stop_ids para mover las paradas.");
      return {
        type: "move-route-stop",
        payload: { sourceRouteId: fromBoardId, routeStopIds, targetRouteId: toBoardId, newOrder: toOrderNumberFromIndex(newIndex) },
      };
    }
    const routeStopId = resolveRouteStopId(sourceRouteStopSnapshot?.apiStop);
    if (!routeStopId) throw new Error("No se encontro _id de route_stop para mover la tarea.");
    return {
      type: "move-route-stop",
      payload: { sourceRouteId: fromBoardId, routeStopIds: [routeStopId], targetRouteId: toBoardId, newOrder: toOrderNumberFromIndex(newIndex) },
    };
  }

  if (!fromIsUnassigned && !toIsUnassigned && sameBoard) {
    if (isMulti) {
      const routeStopIds = collectOrderedCards()
        .map((card) => resolveRouteStopId(card.apiStop))
        .filter(Boolean);
      if (!routeStopIds.length) throw new Error("No se encontraron route_stop_ids para reordenar las paradas.");
      const newIndicies = Array.isArray(event?.newIndicies) ? event.newIndicies : [];
      const effectiveNewIndex = newIndicies.length > 1
        ? Math.min(...newIndicies.map((ni) => ni.index))
        : newIndex;
      return {
        type: "reorder-route-stop",
        payload: { routeId: toBoardId, routeStopIds, newOrder: toOrderNumberFromIndex(effectiveNewIndex) },
      };
    }
    const routeStopId =
      resolveRouteStopId(destinationRouteStop?.apiStop) || resolveRouteStopId(sourceRouteStopSnapshot?.apiStop);
    if (!routeStopId) throw new Error("No se encontro _id de route_stop para reordenar la tarea.");
    return {
      type: "reorder-route-stop",
      payload: { routeId: toBoardId, routeStopIds: [routeStopId], newOrder: toOrderNumberFromIndex(newIndex) },
    };
  }

  clearDragSnapshot();
  return null;
}

function applyLocalDragUpdate(event) {
  const fromBoardId = toSafeString(event?.from?.dataset?.boardId);
  const toBoardId = toSafeString(event?.to?.dataset?.boardId);

  if (!fromBoardId || !toBoardId) return;

  const multiItems = Array.isArray(event?.items) ? event.items.filter(Boolean) : [];
  const isMultiDrag = multiItems.length > 1;
  const draggedIds = isMultiDrag
    ? multiItems.map((el) => toSafeString(el?.dataset?.taskId)).filter(Boolean)
    : [toSafeString(event?.item?.dataset?.taskId)].filter(Boolean);

  if (!draggedIds.length || !draggedIds[0]) return;

  const fromBoard = routeBoards.value.find((b) => b.id === fromBoardId);
  const toBoard = routeBoards.value.find((b) => b.id === toBoardId);

  if (!fromBoard || !toBoard) return;

  const draggedCards = draggedIds
    .map((id) => fromBoard.items.find((c) => toSafeString(c.id) === id))
    .filter(Boolean);

  if (!draggedCards.length) return;

  const filteredFrom = fromBoard.items.filter((c) => !draggedIds.includes(toSafeString(c.id)));
  const rawIndex = parseIndex(event?.newIndex);

  if (fromBoardId === toBoardId) {
    if (typeof document !== "undefined") {
      const boardEl = document.querySelector(`[data-board-id="${fromBoardId}"]`);
      if (boardEl) {
        const domIds = Array.from(boardEl.querySelectorAll(".kanban-dp-item[data-task-id]"))
          .map((el) => toSafeString(el.dataset.taskId));
        const itemMap = new Map(fromBoard.items.map((c) => [toSafeString(c.id), c]));
        const ordered = domIds.map((id) => itemMap.get(id)).filter(Boolean);
        if (ordered.length === fromBoard.items.length) {
          fromBoard.items = ordered;
          return;
        }
      }
    }
    const insertAt = Math.max(0, Math.min(rawIndex, filteredFrom.length));
    filteredFrom.splice(insertAt, 0, ...draggedCards);
    fromBoard.items = filteredFrom;
  } else {
    fromBoard.items = filteredFrom;
    const newToItems = toBoard.items.filter((c) => !draggedIds.includes(toSafeString(c.id)));
    const insertAt = Math.max(0, Math.min(rawIndex, newToItems.length));
    newToItems.splice(insertAt, 0, ...draggedCards);
    toBoard.items = newToItems;
  }
}

export function usePlannerKanban() {
  return {
    routeBoards,
    vehicleOptions,
    setVehicleOptions,
    setUnassignedStops,
    setPlanContext,
    clearPlanContext,
    unassignedStopsBoard,
    assignedRouteBoards,
    getRouteDragGroup,
    getRouteAccentColor,
    syncRouteBoardStats,
    getRouteVehicleContext,
    handleDragStart,
    clearDragSnapshot,
    restoreDragSnapshot,
    resolveDropAction,
    applyLocalDragUpdate,
  };
}
